import React, { useState, useEffect } from 'react';
import { CalendarComponent } from '@src/components/Calendar';
import {
  useAppointmentsFormFields,
  openPatientProfileInNewTab,
} from '../hooks';
import { usePatientFormFields, CreateForm } from '@src/components/shared/forms';
import { Button, Tag, Spin, Tooltip } from 'antd';
import { UserOutlined, DollarOutlined, PlusOutlined } from '@ant-design/icons';
import { useInvalidate } from '@refinedev/core';
import { usePatientDues, useClinicDues } from '@src/hooks';
import { cleanPhoneNumber } from '@src/utils/phoneUtils';

interface AppointmentsCalendarProps {
  onViewChange: (view: 'list' | 'calendar') => void;
  viewTabs: React.ReactNode;
  patientId?: string | null;
  onCreateDrawerClose?: () => void;
}

export const AppointmentsCalendar: React.FC<AppointmentsCalendarProps> = ({
  onViewChange,
  viewTabs,
  patientId,
  onCreateDrawerClose,
}) => {
  const invalidate = useInvalidate();
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [createPatientFormVisible, setCreatePatientFormVisible] =
    useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingRecord, setEditingRecord] = useState<any>(null);

  const formFields = useAppointmentsFormFields(
    patientId || undefined,
    editingRecord
  );
  const patientFormFields = usePatientFormFields();

  // Clean up editing record when form closes
  useEffect(() => {
    if (!editingRecord) return;

    // Reset editing record after a delay to allow form to close
    const timer = setTimeout(() => {
      setEditingRecord(null);
    }, 1000);

    return () => clearTimeout(timer);
  }, [editingRecord]);

  // Get patient dues for the selected record
  const { dues: patientDues, loading: patientDuesLoading } = usePatientDues(
    selectedRecord?.patient_id || null
  );

  // Get clinic dues for the selected record
  const { dues: clinicDues, loading: clinicDuesLoading } = useClinicDues(
    selectedRecord?.patient_id || null
  );

  const handlePatientRedirect = (id: string | number) => {
    openPatientProfileInNewTab(id);
  };

  // Function to handle create patient
  const handleCreatePatient = () => {
    setCreatePatientFormVisible(true);
  };

  // Get patient data from selected record for pre-filling the patient form
  const getPatientInitialValues = () => {
    if (!selectedRecord) return {};

    // Extract patient data from the appointment record
    const patientName = selectedRecord.patient_name || '';

    // Handle mobile number with proper null checks using cleanPhoneNumber utility
    const patientMobile = cleanPhoneNumber(
      selectedRecord.patient_mobile_no || ''
    );

    // Split patient name into first and last name
    const nameParts = patientName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return {
      first_name: firstName,
      last_name: lastName,
      mobile_no: patientMobile,
      appointment_id: selectedRecord.id, // Include appointment_id in the form
    };
  };

  // Function to handle patient creation success
  const handlePatientCreated = (data: any) => {
    setCreatePatientFormVisible(false);

    // Update the selected record with the new patient data
    if (selectedRecord && data?.data?.data) {
      const newPatient = data.data.data; // Access the actual patient data

      const updatedRecord = {
        ...selectedRecord,
        patient_id: newPatient.id,
        patient_name:
          `${newPatient.first_name || ''} ${newPatient.last_name || ''}`.trim(),
        patient_mobile_no: newPatient.mobile_no,
      };

      setSelectedRecord(updatedRecord);

      // Force form refresh by updating the key
      // This will make the CalendarComponent re-render with updated data
      setFormKey(prev => prev + 1);

      // Invalidate appointments cache to refresh the appointment form
      // This will make the appointment form reflect the new patient_id
      invalidate({
        resource: 'appointments',
        invalidates: ['list', 'many'],
      });

      // Also invalidate patients cache to ensure fresh data
      invalidate({
        resource: 'patients',
        invalidates: ['list', 'many'],
      });
    }
  };

  // Create custom title component with View Patient button and Due Amount tag
  const createCustomTitle = (patientId: string | number | null) => {
    const patientDueAmount = patientDues?.total_due || 0;
    const clinicDueAmount = clinicDues?.total_due || 0;
    const hasPatientDues = patientDueAmount > 0;
    const hasClinicDues = clinicDueAmount > 0;
    const isLoading = patientDuesLoading || clinicDuesLoading;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>Edit Appointment</span>
          {patientId !== 0 && (hasPatientDues || hasClinicDues) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isLoading ? (
                <Spin size="small" />
              ) : (
                <>
                  {/* Patient Dues Tag */}
                  {hasPatientDues && (
                    <Tooltip
                      title={`Patient Due: ₹${patientDueAmount.toLocaleString()}`}
                      placement="top"
                    >
                      <Tag
                        color="orange"
                        icon={<DollarOutlined />}
                        style={{ margin: 0, cursor: 'pointer' }}
                      >
                        Patient: ₹ {patientDueAmount.toLocaleString()}
                      </Tag>
                    </Tooltip>
                  )}

                  {/* Clinic Dues Tag */}
                  {hasClinicDues && (
                    <Tooltip
                      title={`Clinic Due: ₹${clinicDueAmount.toLocaleString()}`}
                      placement="top"
                    >
                      <Tag
                        color="red"
                        icon={<DollarOutlined />}
                        style={{ margin: 0, cursor: 'pointer' }}
                      >
                        Clinic: ₹ {clinicDueAmount.toLocaleString()}
                      </Tag>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {patientId ? (
          <Button
            type="primary"
            icon={<UserOutlined />}
            size="small"
            onClick={() => handlePatientRedirect(patientId)}
            style={{ marginLeft: '16px' }}
          >
            View Patient
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="small"
            onClick={handleCreatePatient}
            style={{ marginLeft: '16px' }}
          >
            Create Patient
          </Button>
        )}
      </div>
    );
  };

  // Custom status configuration for appointments
  const statusConfig = {
    colors: {
      CONFIRMED: '#52c41a',
      PENDING: '#faad14',
      CANCELLED: '#ff4d4f',
      COMPLETED: '#1890ff',
    },
    bgColors: {
      CONFIRMED: '#f6ffed',
      PENDING: '#fffbe6',
      CANCELLED: '#fff2f0',
      COMPLETED: '#f0f9ff',
    },
    legend: [
      { status: 'CONFIRMED', label: 'Confirmed', color: '#52c41a' },
      { status: 'PENDING', label: 'Pending', color: '#faad14' },
      { status: 'CANCELLED', label: 'Cancelled', color: '#ff4d4f' },
      { status: 'COMPLETED', label: 'Completed', color: '#1890ff' },
    ],
  };

  // Calendar configuration
  const calendarConfig = {
    showStatusLegend: true,
    showCreateButton: true,
    createButtonText: 'Create Appointment',
    dateFormat: 'YYYY-MM-DDTHH:mm:00',
    timeFormat: 'HH:mm',
    showViewSelector: true,
    defaultView: 'day' as const,
  };

  

  return (
    <div className="flex-1 overflow-hidden">
      <CalendarComponent
        key={formKey}
        calendarStateStorageKey="clinovia-appointments-calendar"
        resource="appointments"
        eventFields={{
          dateField: 'scheduled_datetime',
          nameField: 'patient_name',
          statusField: 'status',
          serviceField: 'service_type',
        }}
        createForm={{
          fields: formFields as any,
          title: 'Create Appointment',
          submitText: 'Create Appointment',
          initialValues: patientId ? { patient_id: patientId } : undefined,
        }}
        editForm={{
          fields: formFields as any,
          title: 'Edit Appointment',
          submitText: 'Save Changes',
          customTitle: (selectedEvent: any) => {
            // Set both records for proper data flow
            setSelectedRecord(selectedEvent);
            setEditingRecord(selectedEvent);
            return createCustomTitle(selectedEvent?.patient_id);
          },
        }}
        formDisplayMode="drawer"
        drawerWidth={1200}
        showCreate={true}
        showEdit={true}
        showDelete={true}
        statusConfig={statusConfig}
        calendarConfig={calendarConfig}
        viewTabs={viewTabs}
        enableChat={true}
        chatType="APPOINTMENT"
        chatPatientId={
          selectedRecord?.patient_id ||
          (patientId ? parseInt(patientId) : undefined)
        }
        chatTitle={
          selectedRecord?.patient_id
            ? 'Patient Messages'
            : 'Appointment Messages'
        }
        chatPlaceholder={
          selectedRecord?.patient_id
            ? 'Add a note for patient...'
            : 'Add a note for appointment...'
        }
        isintialScroll={true}
        // Use patient_id as chatRelativeId - prioritize selectedRecord patient_id, fallback to URL patientId
        chatRelativeId={
          selectedRecord?.patient_id ||
          (patientId ? parseInt(patientId) : undefined)
        }
        onCreateSuccess={() => {
          // Handle create success
        }}
        onEditSuccess={() => {
          // Handle edit success
        }}
        autoOpenCreate={!!patientId}
        onCreateDrawerClose={onCreateDrawerClose}
        onEditClick={record => {
          setEditingRecord(record);
          setSelectedRecord(record);
        }}
      />

      {/* Patient Creation Form */}
      <CreateForm
        resource="patients"
        fields={patientFormFields}
        title="Create New Patient"
        submitText="Create Patient"
        open={createPatientFormVisible}
        onClose={() => setCreatePatientFormVisible(false)}
        onSuccess={handlePatientCreated}
        formDisplayMode="modal"
        modalWidth={800}
        initialValues={getPatientInitialValues()}
      />
    </div>
  );
};
